import React from 'react';
import Card from '../../../components/ui/Card';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';

const ChurnProbabilityList = ({ churnData = [], aiAnalysis = null, loading = false }) => {
  const getRiskColor = (category) => {
    switch (category) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      default:
        return 'success';
    }
  };

  const getRiskBgColor = (category) => {
    switch (category) {
      case 'critical':
        return 'bg-danger-50 border-danger-200';
      case 'high':
        return 'bg-danger-50 border-danger-200';
      case 'medium':
        return 'bg-warning-50 border-warning-200';
      default:
        return 'bg-success-50 border-success-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Analysis Summary */}
      {aiAnalysis?.analyses?.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-100 rounded-lg">
              <Icon name="Brain" className="w-5 h-5 text-warning-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">OpenAI Churn Risk Analysis</h3>
              <p className="text-sm text-muted-foreground">GPT-5 Powered Tenant Retention Insights</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-danger-600">
                {aiAnalysis?.analyses?.filter(a => a?.riskCategory === 'critical' || a?.riskCategory === 'high')?.length || 0}
              </div>
              <div className="text-xs text-muted-foreground">High Risk</div>
            </div>
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-warning-600">
                {aiAnalysis?.analyses?.filter(a => a?.riskCategory === 'medium')?.length || 0}
              </div>
              <div className="text-xs text-muted-foreground">Medium Risk</div>
            </div>
            <div className="bg-success-50 border border-success-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-success-600">
                {aiAnalysis?.analyses?.filter(a => a?.riskCategory === 'low')?.length || 0}
              </div>
              <div className="text-xs text-muted-foreground">Low Risk</div>
            </div>
          </div>
        </Card>
      )}

      {loading && !aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Analyzing churn risk with OpenAI...</p>
            </div>
          </div>
        </Card>
      )}

      {/* Detailed Tenant List */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Tenant Churn Probability Details</h3>
        {aiAnalysis?.analyses?.length > 0 ? (
          <div className="space-y-4">
            {aiAnalysis?.analyses?.map((analysis, index) => {
              const tenant = churnData?.find(t => t?.id === analysis?.tenantId);
              return (
                <div key={index} className={`p-4 rounded-lg border ${getRiskBgColor(analysis?.riskCategory)}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">
                          {tenant?.companyName || tenant?.name || 'Unknown Tenant'}
                        </h4>
                        <Badge variant={getRiskColor(analysis?.riskCategory)}>
                          {analysis?.riskCategory?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {tenant?.unitNumber ? `Unit ${tenant?.unitNumber}` : ''} 
                        {tenant?.monthsRemaining ? `• ${tenant?.monthsRemaining} months remaining` : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-danger-600">
                        {analysis?.churnProbability?.toFixed(0)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Churn Risk</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h5 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
                      <Icon name="AlertTriangle" className="w-4 h-4" />
                      Risk Factors
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {analysis?.riskFactors?.map((factor, idx) => (
                        <span key={idx} className="text-xs bg-white border border-border rounded px-2 py-1">
                          {factor}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white bg-opacity-50 rounded-lg p-3">
                    <h5 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
                      <Icon name="Target" className="w-4 h-4" />
                      AI Retention Strategy
                    </h5>
                    <p className="text-sm text-foreground">
                      {analysis?.retentionStrategy}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="Users" className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No churn probability data available</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChurnProbabilityList;